"use client";

import { useState, useId, useRef } from "react";
import { motion, useReducedMotion } from "@/lib/motion";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import { EyebrowBadge } from "@/components/ui/eyebrow-badge";
import { HeroGlow, FormError, FormSuccessPage, brandActionClasses } from "@/components/ui";
import { cn } from "@/lib/utils";
import { useFormik } from "formik";
import { toast } from "sonner";
import { ArrowRight, Loader2, Upload, X } from "@/components/icons";
import { EMAIL } from "@/lib/constants";
import { getFieldError } from "@/lib/form-helpers";
import { reportAbuseSchema } from "@/lib/form-schemas";
import { AccuracyCheckbox, DestinationUrlsField, ReportTypeSelect } from "./parts";
import type { ReportAbuseFormValues } from "./types";

type FormState = "idle" | "submitting" | "success" | "error";

export default function ReportAbuseSection() {
  const prefersReducedMotion = useReducedMotion();
  const formId = useId();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formState, setFormState] = useState<FormState>("idle");
  const [files, setFiles] = useState<File[]>([]);

  const formik = useFormik<ReportAbuseFormValues>({
    initialValues: {
      reportType: "",
      fullName: "",
      email: "",
      incidentDate: "",
      ips: "",
      evidence: "",
      destinationUrls: [""],
      attestation: false,
      company: "",
    },
    validationSchema: reportAbuseSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setFormState("submitting");
      try {
        const body = new FormData();
        body.append("reportType", values.reportType);
        body.append("fullName", values.fullName);
        body.append("email", values.email);
        body.append("incidentDate", values.incidentDate);
        body.append("ips", values.ips);
        body.append("evidence", values.evidence);
        body.append("attestation", String(values.attestation));
        values.destinationUrls
          .map((url) => url.trim())
          .filter(Boolean)
          .forEach((url) => body.append("destinationUrls", url));
        body.append("company", values.company); // honeypot — legitimate users leave this empty
        files.forEach((file) => body.append("files", file));

        const res = await fetch("/api/contact/report-abuse", { method: "POST", body });
        if (!res.ok) throw new Error("error");

        toast.success("Report submitted", {
          description: "Our compliance team will review it within one business day.",
        });
        setFormState("success");
      } catch {
        toast.error("Something went wrong", {
          description: `Please try again or email ${EMAIL.abuse}`,
        });
        setFormState("error");
      } finally {
        setSubmitting(false);
      }
    },
  });

  function scalarError(
    field: "fullName" | "email" | "incidentDate" | "ips" | "evidence"
  ): string | undefined {
    return getFieldError(formik.touched, formik.errors, field);
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const selectedFiles = e.target.files;
    if (!selectedFiles) return;
    setFiles((prev) => [...prev, ...Array.from(selectedFiles)]);
  }

  function removeFile(index: number) {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }

  const inputClass =
    "w-full rounded-lg border border-border px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-foreground/20 transition-colors";
  const inputStyle = {
    backgroundColor: "color-mix(in srgb, var(--foreground) 3%, transparent)",
  };

  if (formState === "success") {
    return (
      <FormSuccessPage
        title="Report received"
        description="Thank you for contacting the Proxidize Compliance Team. We will review your report and respond within one business day."
      />
    );
  }

  return (
    <main id="main-content" tabIndex={-1} className="overflow-y-clip pb-32">
      <section
        className="border-border relative overflow-y-clip border-b pt-32 pb-20"
        aria-labelledby="report-abuse-heading"
      >
        <HeroGlow align="center" />

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <motion.div
              initial={prefersReducedMotion ? false : { opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <EyebrowBadge>Compliance</EyebrowBadge>
            </motion.div>

            <motion.h1
              id="report-abuse-heading"
              initial={prefersReducedMotion ? false : { opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.08 }}
              className="text-foreground mb-5 text-4xl leading-[1.1] font-bold tracking-tight sm:text-5xl lg:text-6xl"
            >
              Report Abuse Activity
            </motion.h1>

            <motion.p
              initial={prefersReducedMotion ? false : { opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="text-muted-foreground light:text-foreground/80 mx-auto max-w-xl text-base leading-relaxed sm:text-lg"
            >
              Proxidize enforces a strict zero-tolerance policy toward any non-compliant traffic or
              misuse of our network. If you believe Proxidize IP addresses are being used for
              abusive activity, please notify the Proxidize Compliance Team through the form below.
            </motion.p>

            <motion.p
              initial={prefersReducedMotion ? false : { opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.22 }}
              className="text-muted-foreground mx-auto mt-4 max-w-lg text-sm leading-relaxed"
            >
              For the fastest response, make sure to complete every field and include all relevant
              details. We will respond within one business day.
            </motion.p>
          </div>
        </div>
      </section>

      <section className="mx-auto mt-16 mb-8 max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl">
          <div className="">
            <motion.form
              id={`${formId}-form`}
              onSubmit={formik.handleSubmit}
              initial={prefersReducedMotion ? false : { opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="flex flex-col gap-5"
              noValidate
            >
              <input
                type="text"
                name="company"
                value={formik.values.company}
                onChange={formik.handleChange}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="sr-only"
              />

              <ReportTypeSelect formId={formId} formik={formik} />

              <div>
                <label
                  htmlFor={`${formId}-fullName`}
                  className="text-muted-foreground mb-2 block text-xs font-semibold tracking-wider uppercase"
                >
                  Full Name <span className="text-red-400">*</span>
                </label>
                <input
                  id={`${formId}-fullName`}
                  name="fullName"
                  type="text"
                  placeholder="Jane Smith"
                  value={formik.values.fullName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={inputClass}
                  style={inputStyle}
                />
                {scalarError("fullName") && (
                  <FormError className="mt-1">{scalarError("fullName")}</FormError>
                )}
              </div>

              <div>
                <label
                  htmlFor={`${formId}-email`}
                  className="text-muted-foreground mb-2 block text-xs font-semibold tracking-wider uppercase"
                >
                  Email <span className="text-red-400">*</span>
                </label>
                <input
                  id={`${formId}-email`}
                  name="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={inputClass}
                  style={inputStyle}
                />
                {scalarError("email") && (
                  <FormError className="mt-1">{scalarError("email")}</FormError>
                )}
              </div>

              <div>
                <label
                  htmlFor={`${formId}-incidentDate`}
                  className="text-muted-foreground mb-2 block text-xs font-semibold tracking-wider uppercase"
                >
                  Incident Date <span className="text-red-400">*</span>
                </label>
                <input
                  id={`${formId}-incidentDate`}
                  name="incidentDate"
                  type="date"
                  value={formik.values.incidentDate}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={inputClass}
                  style={inputStyle}
                />
                {scalarError("incidentDate") && (
                  <FormError className="mt-1">{scalarError("incidentDate")}</FormError>
                )}
              </div>

              <div>
                <label
                  htmlFor={`${formId}-ips`}
                  className="text-muted-foreground mb-2 block text-xs font-semibold tracking-wider uppercase"
                >
                  IPs <span className="text-red-400">*</span>
                </label>
                <input
                  id={`${formId}-ips`}
                  name="ips"
                  type="text"
                  placeholder="e.g. 192.168.1.1, 10.0.0.1"
                  value={formik.values.ips}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={inputClass}
                  style={inputStyle}
                />
                <p className="text-muted-foreground mt-1.5 text-xs">
                  Separate multiple IP addresses with commas.
                </p>
                {scalarError("ips") && <FormError className="mt-1">{scalarError("ips")}</FormError>}
              </div>

              <div>
                <label
                  htmlFor={`${formId}-evidence`}
                  className="text-muted-foreground mb-2 block text-xs font-semibold tracking-wider uppercase"
                >
                  Evidence of Abuse{" "}
                  {formik.values.reportType !== "account_security" && (
                    <span className="text-red-400">*</span>
                  )}
                </label>
                <textarea
                  id={`${formId}-evidence`}
                  name="evidence"
                  rows={5}
                  placeholder="Describe the abusive activity in detail — include timestamps, affected systems, and any other relevant information."
                  value={formik.values.evidence}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={`${inputClass} resize-none`}
                  style={inputStyle}
                />
                {scalarError("evidence") && (
                  <FormError className="mt-1">{scalarError("evidence")}</FormError>
                )}
              </div>

              <DestinationUrlsField formId={formId} formik={formik} />

              <div>
                <label className="text-muted-foreground mb-2 block text-xs font-semibold tracking-wider uppercase">
                  Supporting Images
                </label>

                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="border-border/70 bg-foreground/[0.02] hover:border-foreground/20 hover:bg-foreground/[0.02] flex w-full flex-col items-center gap-2 rounded-lg border border-dashed px-4 py-6 transition-colors"
                >
                  <Upload size={18} className="text-muted-foreground" />
                  <span className="text-muted-foreground text-sm">
                    Click to upload screenshots or log files
                  </span>
                  <span className="text-muted-foreground text-xs">
                    PNG, JPG, GIF, PDF up to 10 MB each
                  </span>
                </button>

                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*,.pdf"
                  onChange={handleFileChange}
                  className="sr-only"
                  aria-label="Upload supporting images"
                />

                {files.length > 0 && (
                  <ul className="mt-3 flex flex-col gap-2">
                    {files.map((file, fileIdx) => (
                      <li
                        key={file.name}
                        className="border-border/60 bg-foreground/[0.02] flex items-center justify-between gap-3 rounded-lg border px-3 py-2"
                      >
                        <span className="text-muted-foreground truncate text-xs">{file.name}</span>
                        <button
                          type="button"
                          onClick={() => removeFile(fileIdx)}
                          className="text-muted-foreground hover:text-foreground shrink-0 transition-colors"
                          aria-label={`Remove ${file.name}`}
                        >
                          <X size={14} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <AccuracyCheckbox formId={formId} formik={formik} />

              <button
                type="submit"
                disabled={formState === "submitting" || formik.isSubmitting}
                className={cn(
                  brandActionClasses,
                  "mt-1 flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg px-6 py-3 text-sm font-semibold transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-50"
                )}
              >
                {formState === "submitting" ? (
                  <>
                    <Loader2 size={15} className="animate-spin" />
                    Submitting…
                  </>
                ) : (
                  <>
                    Submit Report
                    <ArrowRight size={14} />
                  </>
                )}
              </button>
            </motion.form>
          </div>

          <motion.p
            initial={prefersReducedMotion ? false : { opacity: 0, y: 8 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={scrollRevealViewport}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="text-muted-foreground mt-6 text-center text-sm"
          >
            Should you have trouble submitting the form, email us at{" "}
            <a
              href={`mailto:${EMAIL.abuse}`}
              className="text-foreground hover:text-primary font-semibold transition-colors duration-200"
            >
              {EMAIL.abuse}
            </a>
          </motion.p>
        </div>
      </section>
    </main>
  );
}
